import AccountPreferences from '../components/AccountPreferences';
import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './subscribe.css';
import './settings.css';
import ConfirmDialog from '../components/ConfirmDialog';

function readUser() {
  try { return JSON.parse(localStorage.getItem('adeeb-user') || 'null'); } catch { return null; }
}

function authHeaders() {
  const token = localStorage.getItem('adeeb-token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function fmtDate(d) {
  if (!d) return '—';
  const date = new Date(d);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
}

const TIERS = [
  {
    id: 'standard',
    name: 'Standard',
    price: '£7.50',
    period: '/ month',
    profiles: 'Up to 3 profiles',
    features: [
      'Full library access',
      'Audio narration with synced highlighting',
      'Quizzes, achievements & rewards',
      'Cancel any time',
    ],
  },
  {
    id: 'family',
    name: 'Family',
    price: '£10',
    period: '/ month',
    profiles: 'Up to 6 profiles',
    badge: 'Best value',
    features: [
      'Everything in Standard',
      'Up to 6 reader profiles for the family',
      'Each child gets their own progress, favourites & rewards',
      'Cancel any time',
    ],
  },
];

const TickIcon = () => (
  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="5 12 10 17 19 7" />
  </svg>
);
const WarnIcon = () => (
  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3l10 18H2L12 3z" />
    <line x1="12" y1="10" x2="12" y2="14" />
    <circle cx="12" cy="17.5" r="0.4" fill="currentColor" />
  </svg>
);

export default function Settings() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = readUser();

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // 'standard' | 'family' | 'portal' | 'cancel' | 'resume'
  const [error, setError] = useState('');
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [pendingSwitch, setPendingSwitch] = useState(null); // tier id being confirmed

  const refresh = () => {
    return axios
      .get('/api/billing/status', { headers: authHeaders() })
      .then(({ data }) => setStatus(data))
      .catch(() => setStatus(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!localStorage.getItem('adeeb-token')) {
      navigate(`/login?redirect=${encodeURIComponent(location.pathname + location.hash)}`, { replace: true });
      return;
    }
    refresh();
    // Returning from email should not remount an open confirmation dialog or
    // discard a deletion code the reader is about to paste.
    const onFocus = () => {
      if (document.querySelector('.confirm-backdrop')) return;
      refresh();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // Note: scroll-lock + Esc handling for the cancel confirm dialog live
  // inside ConfirmDialog itself — duplicating them here would re-restore
  // overflow:hidden during the close transition and lock scrolling.

  if (!user) return null;

  const handleSubscribe = async (tierId) => {
    setBusy(tierId);
    setError('');
    try {
      const { data } = await axios.post(
        '/api/billing/checkout',
        { next: '/settings', tier: tierId },
        { headers: authHeaders() }
      );
      window.location.href = data.url;
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not start checkout.');
      setBusy(null);
    }
  };

  const handlePortal = async () => {
    setError('');
    setBusy('portal');
    try {
      const { data } = await axios.post('/api/billing/portal', { return: '/settings' }, { headers: authHeaders() });
      window.location.href = data.url;
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not open billing portal.');
      setBusy(null);
    }
  };

  const performCancel = async () => {
    setError('');
    setBusy('cancel');
    try {
      await axios.post('/api/billing/cancel', {}, { headers: authHeaders() });
      await refresh();
      setConfirmCancel(false);
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not cancel subscription.');
    } finally {
      setBusy(null);
    }
  };

  const handleSwitchTier = async (tierId) => {
    setError('');
    setBusy(`switch-${tierId}`);
    try {
      await axios.post('/api/billing/switch-tier', { tier: tierId }, { headers: authHeaders() });
      await refresh();
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not switch plan.');
    } finally {
      // Close the confirm dialog regardless of outcome so the user sees
      // either the refreshed page state (success) or the error banner
      // (e.g. PROFILE_CAP_EXCEEDED on a Family → Standard downgrade).
      setPendingSwitch(null);
      setBusy(null);
    }
  };

  const handleResume = async () => {
    setError('');
    setBusy('resume');
    try {
      await axios.post('/api/billing/resume', {}, { headers: authHeaders() });
      await refresh();
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not resume subscription.');
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="sub-page">
        <div className="sub-loading"><div className="sub-spinner" /><p>Loading account…</p></div>
      </div>
    );
  }

  const isActive = !!status?.active;
  const isCancelling = !!status?.cancelAtPeriodEnd;

  // The server already knows the price-id → tier mapping; just read it.
  const currentTier = status?.currentTier || null;

  return (
    <div className="sub-page">
      <div className="sub-shell sub-shell--wide">
        <Link to="/profiles/select" className="sub-back">← Back to profiles</Link>

        <header className="sub-hero">
          <div className="sub-eyebrow">Account settings</div>
          <h1 className="sub-title">Hi {user.name?.split(' ')[0] || 'there'}</h1>
          <p className="sub-blurb">
            Signed in as <strong>{user.email}</strong>. Manage your subscription
            and billing below — Deenlit never sees or stores your card.
          </p>
        </header>

        {isActive ? (
          // ── Active subscription: status banner + tier picker for switching ──
          <>
            <div className={`sub-notice is-${isCancelling ? 'cancelled' : 'active'}`}
                 style={{ maxWidth: 540, margin: '0 auto' }}>
              <div className="sub-notice-row">
                <span className="sub-notice-icon">{isCancelling ? <WarnIcon /> : <TickIcon />}</span>
                <div>
                  <span className="sub-notice-strong">
                    {isCancelling ? 'Cancellation scheduled' : 'Active subscription'}
                  </span>
                  {isCancelling
                    ? <>You'll keep premium access until {fmtDate(status.currentPeriodEnd)}, then it ends.</>
                    : <>Currently on {currentTier === 'family' ? 'Family' : 'Standard'} · next payment on {fmtDate(status.currentPeriodEnd)}.</>}
                </div>
              </div>
            </div>

            <div className="sub-tier-grid">
              {TIERS.map((tier) => {
                const available = status?.tiers?.[tier.id] !== false;
                const isPopular = tier.id === 'family';
                const isCurrent = currentTier === tier.id;
                const switching = busy === `switch-${tier.id}`;
                return (
                  <div
                    key={tier.id}
                    className={`sub-tier${isPopular ? ' is-popular' : ''}${!available ? ' is-unavailable' : ''}${isCurrent ? ' is-current' : ''}`}
                  >
                    {isCurrent
                      ? <div className="sub-tier-badge sub-tier-badge--current">Current plan</div>
                      : tier.badge && available
                        ? <div className="sub-tier-badge">{tier.badge}</div>
                        : !available
                          ? <div className="sub-tier-badge sub-tier-badge--soon">Coming soon</div>
                          : null}
                    <div className="sub-tier-name">{tier.name}</div>
                    <div className="sub-tier-price">
                      <span className="sub-tier-price-amount">{tier.price}</span>
                      <span className="sub-tier-price-period">{tier.period}</span>
                    </div>
                    <div className="sub-tier-profiles">{tier.profiles}</div>
                    <ul className="sub-tier-features">
                      {tier.features.map((f) => (
                        <li key={f}>
                          <span className="sub-features-check"><TickIcon /></span>
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                    {isCurrent ? (
                      <button type="button" disabled className="sub-cta sub-cta-secondary" style={{ cursor: 'default' }}>
                        Current plan
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => available && setPendingSwitch(tier.id)}
                        disabled={!available || !!busy}
                        className={`sub-cta ${isPopular ? 'sub-cta-primary' : 'sub-cta-secondary'}`}
                      >
                        {!available
                          ? 'Coming soon'
                          : switching
                            ? 'Switching…'
                            : `Switch to ${tier.name}`}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            {error && (
              <div className="sub-notice is-error" style={{ maxWidth: 540, margin: '12px auto 0' }}>
                <div className="sub-notice-row">
                  <span className="sub-notice-icon"><WarnIcon /></span>
                  <div>{error}</div>
                </div>
              </div>
            )}

            <div className="sub-card" style={{ maxWidth: 540, margin: '20px auto 0', padding: '24px 26px 18px' }}>
              <button
                type="button"
                className="sub-cta"
                onClick={handlePortal}
                disabled={busy === 'portal'}
              >
                {busy === 'portal' ? 'Opening billing portal…' : 'Manage subscription / payment method'}
              </button>
              {!isCancelling && (
                <button
                  type="button"
                  className="sub-secondary"
                  onClick={() => { setError(''); setConfirmCancel(true); }}
                  disabled={busy === 'cancel'}
                  style={{ marginTop: 10 }}
                >
                  Cancel subscription
                </button>
              )}
              {isCancelling && (
                <button
                  type="button"
                  className="sub-secondary"
                  onClick={handleResume}
                  disabled={busy === 'resume'}
                  style={{ marginTop: 10 }}
                >
                  {busy === 'resume' ? 'Resuming…' : 'Keep subscription'}
                </button>
              )}
              <div className="sub-fineprint">
                Card details, invoices and payment method changes are managed in the secure Stripe portal.
              </div>
            </div>
          </>
        ) : (
          // ── Unsubscribed: show two-tier picker (matches /subscribe) ──
          <>
            <div className="sub-tier-grid">
              {TIERS.map((tier) => {
                const available = status?.tiers?.[tier.id] !== false;
                const isPopular = tier.id === 'family';
                return (
                  <div key={tier.id} className={`sub-tier${isPopular ? ' is-popular' : ''}${!available ? ' is-unavailable' : ''}`}>
                    {tier.badge && available && <div className="sub-tier-badge">{tier.badge}</div>}
                    {!available && <div className="sub-tier-badge sub-tier-badge--soon">Coming soon</div>}
                    <div className="sub-tier-name">{tier.name}</div>
                    <div className="sub-tier-price">
                      <span className="sub-tier-price-amount">{tier.price}</span>
                      <span className="sub-tier-price-period">{tier.period}</span>
                    </div>
                    <div className="sub-tier-profiles">{tier.profiles}</div>
                    <ul className="sub-tier-features">
                      {tier.features.map((f) => (
                        <li key={f}>
                          <span className="sub-features-check"><TickIcon /></span>
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                    <button
                      type="button"
                      onClick={() => available && handleSubscribe(tier.id)}
                      disabled={!available || !!busy}
                      className={`sub-cta ${isPopular ? 'sub-cta-primary' : 'sub-cta-secondary'}`}
                      aria-disabled={!available}
                    >
                      {!available
                        ? 'Coming soon'
                        : busy === tier.id
                          ? 'Redirecting to Stripe…'
                          : `Choose ${tier.name}`}
                    </button>
                  </div>
                );
              })}
            </div>
            {error && (
              <div className="sub-notice is-error" style={{ maxWidth: 540, margin: '12px auto 0' }}>
                <div className="sub-notice-row">
                  <span className="sub-notice-icon"><WarnIcon /></span>
                  <div>{error}</div>
                </div>
              </div>
            )}
            <div className="sub-fineprint">
              Secured by Stripe · We never store your card details
            </div>
          </>
        )}
        <AccountPreferences />
      </div>

      <ConfirmDialog
        open={!!pendingSwitch}
        tone="neutral"
        title={pendingSwitch ? `Switch to ${pendingSwitch === 'family' ? 'Family' : 'Standard'}?` : ''}
        body={
          pendingSwitch === 'family'
            ? "You'll be billed the prorated difference today and £10/month from your next renewal. Up to 6 reader profiles unlocked immediately."
            : "You'll be credited the prorated difference. Standard limits you to 3 profiles — if you currently have more, you'll need to delete the extras first."
        }
        confirmLabel={pendingSwitch ? `Switch to ${pendingSwitch === 'family' ? 'Family' : 'Standard'}` : ''}
        cancelLabel="Keep current plan"
        busy={busy && busy.startsWith('switch-')}
        onClose={() => { if (!busy || !busy.startsWith('switch-')) setPendingSwitch(null); }}
        onConfirm={() => pendingSwitch && handleSwitchTier(pendingSwitch)}
      />

      <ConfirmDialog
        open={confirmCancel}
        tone="danger"
        title="Cancel your subscription?"
        body={`You'll keep premium access until ${fmtDate(status?.currentPeriodEnd)}, then reading, quizzes and audio narration will be locked until you resubscribe.`}
        confirmLabel="Yes, cancel"
        cancelLabel="Keep my subscription"
        busy={busy === 'cancel'}
        onClose={() => { if (busy !== 'cancel') setConfirmCancel(false); }}
        onConfirm={performCancel}
      />
    </div>
  );
}
